import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import { Combobox } from "design-system/organisms/Combobox"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { useShopifyMetafieldDefinitionsQuery } from "modules/settings/hooks/shopify/useShopifyMetafieldDefinitionsQuery"
import { useShopifySearchProductsQuery } from "modules/settings/hooks/shopify/useShopifySearchProductsQuery"
import {
  FrontendUpdateSizeChartMetafieldSchema,
  useShopifyUpdateSizeChartMetafieldMutation,
} from "modules/settings/hooks/shopify/useShopifyUpdateSizeChartMetafieldMutation"
import { useDebouncedValue } from "modules/shared/hooks/useDebouncedValue"
import { useEffect, useMemo, useRef, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  ConvertSizeChartResponseData,
  ShopifyMetafieldDefinitionsResponseDataSchema,
  ShopifyProductsResponseDataSchema,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"
import type { GeneratedSizeChartImage } from "./ConvertSizeChartGenerateImageForm"
import { getMetafieldSizeChartResult } from "./sizeChartConversion"

type ConvertSizeChartResult = z.infer<typeof ConvertSizeChartResponseData>
type ShopifyMetafieldDefinition = z.infer<
  typeof ShopifyMetafieldDefinitionsResponseDataSchema
>[number]
type ShopifyProduct = z.infer<typeof ShopifyProductsResponseDataSchema>[number]
type ShopifyProductVariant = NonNullable<ShopifyProduct["variants"]>[number]
type UpdateSizeChartMetafieldPayload = z.infer<typeof FrontendUpdateSizeChartMetafieldSchema>
type ProductOption = {
  value: string
  label: string
  image?: string | null
  product: ShopifyProduct
}
type MetafieldOption = ShopifyMetafieldDefinition & {
  label: string
  value: string
}
type ProductSelection = {
  value: string
}

const PRODUCT_METAFIELD_VALUE = "__product__"
const IMAGE_METAFIELD_TYPE = "file_reference"
const PERMUT_CONFIG_METAFIELD_KEY = "permut_config"
const PERMUT_CONFIG_METAFIELD_TYPE = "file_reference"
const SIZE_CHART_METAFIELD_TYPE = "json"
const LAST_IMAGE_METAFIELD_KEY_STORAGE_KEY = "shopify-size-chart-last-image-metafield-key"
const LAST_SIZE_CHART_METAFIELD_KEY_STORAGE_KEY = "shopify-size-chart-last-size-chart-metafield-key"

type Props = {
  generatedImage: GeneratedSizeChartImage | null
  isOpen: boolean
  result: ConvertSizeChartResult | null
}

function getVariantLabel(variant: ShopifyProductVariant) {
  if (variant.title && variant.title !== "Default Title") {
    return variant.title
  }

  const options = variant.selectedOptions.map((option) => option.value).filter(Boolean)
  if (options.length > 0) return options.join(" / ")

  return variant.sku ?? variant.id
}

function getProductLabel(product: ShopifyProduct) {
  return product.status === "DRAFT" ? `[DRAFT] ${product.title}` : product.title
}

function isDefaultOnlyVariant(variant: ShopifyProductVariant) {
  const hasDefaultTitle = !variant.title || variant.title === "Default Title"
  const hasDefaultOptions =
    variant.selectedOptions.length === 0 ||
    variant.selectedOptions.every(
      (option) => option.name === "Title" && option.value === "Default Title",
    )

  return hasDefaultTitle && hasDefaultOptions
}

function getMetafieldOptionLabel(definition: ShopifyMetafieldDefinition) {
  if (!definition.name || definition.name === definition.key) {
    return `${definition.key} (${definition.type})`
  }

  return `${definition.name} - ${definition.key} (${definition.type})`
}

function isPermutConfigMetafieldKey(key: string) {
  return key === PERMUT_CONFIG_METAFIELD_KEY || key.endsWith(`.${PERMUT_CONFIG_METAFIELD_KEY}`)
}

function getUnifiedMetafieldOptions(
  productMetafields: ShopifyMetafieldDefinition[] = [],
  variantMetafields: ShopifyMetafieldDefinition[] = [],
  type?: string,
) {
  const definitions = new Map<string, MetafieldOption>()

  for (const definition of [...productMetafields, ...variantMetafields]) {
    if (type && definition.type !== type) continue
    if (isPermutConfigMetafieldKey(definition.key)) continue
    if (definitions.has(definition.key)) continue

    definitions.set(definition.key, {
      ...definition,
      label: getMetafieldOptionLabel(definition),
      value: definition.key,
    })
  }

  return [...definitions.values()].sort((a, b) => a.label.localeCompare(b.label))
}

function getLocalStorageValue(key: string) {
  return window.localStorage.getItem(key) ?? ""
}

function setLocalStorageValue(key: string, value: string) {
  window.localStorage.setItem(key, value)
}

export function ConvertSizeChartUpdateForm({ generatedImage, isOpen, result }: Props) {
  const { t } = useTranslation()
  const [selectedProduct, setSelectedProduct] = useState<ShopifyProduct | null>(null)
  const [productSearchQuery, setProductSearchQuery] = useState("")
  const lastPrefilledMetafieldValueRef = useRef<string | null>(null)
  const normalizedProductSearchQuery = productSearchQuery.trim().replace(/\s+/g, " ")
  const debouncedProductSearchQuery = useDebouncedValue({
    value: normalizedProductSearchQuery,
  })

  const form = useForm<UpdateSizeChartMetafieldPayload>({
    resolver: zodResolver(FrontendUpdateSizeChartMetafieldSchema),
    mode: "onChange",
    defaultValues: {
      imageMetafieldKey: "",
      metafieldValue: "",
      productId: "",
      sizeChartMetafieldKey: "",
      variantId: undefined,
    },
  })

  const { mutateAsync: updateSizeChartMetafield, isPending: isUpdatingMetafield } =
    useShopifyUpdateSizeChartMetafieldMutation()
  const { data: products, isFetching: isFetchingProducts } = useShopifySearchProductsQuery({
    enabled: isOpen,
    includeDraft: true,
    query: debouncedProductSearchQuery,
  })
  const { data: productMetafields, isFetching: isFetchingProductMetafields } =
    useShopifyMetafieldDefinitionsQuery(isOpen ? "product" : null)
  const { data: variantMetafields, isFetching: isFetchingVariantMetafields } =
    useShopifyMetafieldDefinitionsQuery(isOpen ? "productVariant" : null)
  const isFetchingMetafields = isFetchingProductMetafields || isFetchingVariantMetafields

  const isWaitingForProducts =
    isOpen && (normalizedProductSearchQuery !== debouncedProductSearchQuery || isFetchingProducts)

  const productOptions: ProductOption[] =
    normalizedProductSearchQuery !== debouncedProductSearchQuery
      ? []
      : (products ?? []).map((product) => ({
          value: product.id,
          label: getProductLabel(product),
          image: product.imageUrl,
          product,
        }))

  const selectedProductOption = selectedProduct
    ? {
        value: selectedProduct.id,
        label: getProductLabel(selectedProduct),
        image: selectedProduct.imageUrl,
        product: selectedProduct,
      }
    : undefined
  const variants = selectedProduct?.variants ?? []
  const hasVariant = variants[0]
  const visibleVariants = hasVariant && isDefaultOnlyVariant(hasVariant) ? [] : variants
  const sizeChartMetafieldOptions = useMemo(
    () =>
      getUnifiedMetafieldOptions(productMetafields, variantMetafields, SIZE_CHART_METAFIELD_TYPE),
    [productMetafields, variantMetafields],
  )
  const imageMetafieldOptions = useMemo(
    () => getUnifiedMetafieldOptions(productMetafields, variantMetafields, IMAGE_METAFIELD_TYPE),
    [productMetafields, variantMetafields],
  )

  let emptyProductPlaceholder = t(
    "settings.integrations.shopify.convertSizeChart.form.productEmpty",
  )

  if (isWaitingForProducts) {
    emptyProductPlaceholder = t(
      "settings.integrations.shopify.convertSizeChart.form.productLoading",
    )
  } else if (normalizedProductSearchQuery.length === 0) {
    emptyProductPlaceholder = t("settings.integrations.shopify.convertSizeChart.form.productIdle")
  }

  useEffect(() => {
    if (!isOpen) {
      setSelectedProduct(null)
      setProductSearchQuery("")
      form.reset({
        imageMetafieldKey: "",
        metafieldValue: "",
        productId: "",
        sizeChartMetafieldKey: "",
        variantId: undefined,
      })
      lastPrefilledMetafieldValueRef.current = null
    }
  }, [form, isOpen])

  useEffect(() => {
    const nextMetafieldValue = result
      ? JSON.stringify(getMetafieldSizeChartResult(result.result), null, 2)
      : ""

    if (lastPrefilledMetafieldValueRef.current === nextMetafieldValue) return

    lastPrefilledMetafieldValueRef.current = nextMetafieldValue
    form.setValue("metafieldValue", nextMetafieldValue, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }, [form, result])

  useEffect(() => {
    const selectedSizeChartMetafieldKey = form.getValues("sizeChartMetafieldKey")
    const selectedImageMetafieldKey = form.getValues("imageMetafieldKey")

    if (
      selectedSizeChartMetafieldKey &&
      !sizeChartMetafieldOptions.some(
        (metafield) => metafield.key === selectedSizeChartMetafieldKey,
      )
    ) {
      form.setValue("sizeChartMetafieldKey", "", { shouldValidate: true })
    }

    if (
      selectedImageMetafieldKey &&
      !imageMetafieldOptions.some((metafield) => metafield.key === selectedImageMetafieldKey)
    ) {
      form.setValue("imageMetafieldKey", "", { shouldValidate: true })
    }
  }, [form, imageMetafieldOptions, sizeChartMetafieldOptions])

  useEffect(() => {
    const selectedSizeChartMetafieldKey = form.getValues("sizeChartMetafieldKey")
    const selectedImageMetafieldKey = form.getValues("imageMetafieldKey")
    const lastSizeChartMetafieldKey = getLocalStorageValue(
      LAST_SIZE_CHART_METAFIELD_KEY_STORAGE_KEY,
    )
    const lastImageMetafieldKey = getLocalStorageValue(LAST_IMAGE_METAFIELD_KEY_STORAGE_KEY)

    if (
      !selectedSizeChartMetafieldKey &&
      lastSizeChartMetafieldKey &&
      sizeChartMetafieldOptions.some((metafield) => metafield.key === lastSizeChartMetafieldKey)
    ) {
      form.setValue("sizeChartMetafieldKey", lastSizeChartMetafieldKey, {
        shouldValidate: true,
      })
    }

    if (
      !selectedImageMetafieldKey &&
      lastImageMetafieldKey &&
      imageMetafieldOptions.some((metafield) => metafield.key === lastImageMetafieldKey)
    ) {
      form.setValue("imageMetafieldKey", lastImageMetafieldKey, {
        shouldValidate: true,
      })
    }
  }, [form, imageMetafieldOptions, sizeChartMetafieldOptions])

  const handleProductSearchQueryChange = (value: string) => {
    setProductSearchQuery(value)

    if (selectedProduct && value !== selectedProduct.title) {
      setSelectedProduct(null)
      form.setValue("productId", "", { shouldValidate: true, shouldDirty: true })
      form.setValue("variantId", undefined, { shouldValidate: true, shouldDirty: true })
    }
  }

  const onSubmit = async (data: UpdateSizeChartMetafieldPayload) => {
    const variantId = data.variantId?.trim() || undefined
    const sizeChartMetafield = sizeChartMetafieldOptions.find(
      (metafield) => metafield.key === data.sizeChartMetafieldKey,
    )
    const imageMetafield = imageMetafieldOptions.find(
      (metafield) => metafield.key === data.imageMetafieldKey,
    )

    if (!sizeChartMetafield || !imageMetafield || !generatedImage?.base64Image || !result) return

    await updateSizeChartMetafield({
      productId: data.productId,
      variantId,
      metafields: [
        {
          key: sizeChartMetafield.key,
          type: sizeChartMetafield.type,
          value: data.metafieldValue,
        },
        {
          base64Image: generatedImage.base64Image,
          key: imageMetafield.key,
          type: imageMetafield.type,
        },
        {
          key: PERMUT_CONFIG_METAFIELD_KEY,
          type: PERMUT_CONFIG_METAFIELD_TYPE,
          jsonFile: JSON.stringify({
            version: 1,
            table: result.result,
            imageUrl: generatedImage.url,
            imageTransform: generatedImage.imageTransform,
            labels: generatedImage.dimensions,
          }),
        },
      ],
    })
    setLocalStorageValue(LAST_SIZE_CHART_METAFIELD_KEY_STORAGE_KEY, sizeChartMetafield.key)
    setLocalStorageValue(LAST_IMAGE_METAFIELD_KEY_STORAGE_KEY, imageMetafield.key)
    toast.success(t("settings.integrations.shopify.convertSizeChart.editSuccess"))
  }

  const handleProductChange = (option: ProductSelection | undefined) => {
    const product =
      productOptions.find((candidate) => candidate.value === option?.value)?.product ??
      (selectedProduct?.id === option?.value ? selectedProduct : null)

    setSelectedProduct(product)
    form.setValue("productId", product?.id ?? "", { shouldValidate: true, shouldDirty: true })
    form.setValue("variantId", undefined, { shouldValidate: true, shouldDirty: true })
    setProductSearchQuery(product?.title ?? "")
  }

  return (
    <Form
      {...form}
      onSubmit={form.handleSubmit(onSubmit)}
      className="flex flex-col gap-4 overflow-auto"
      errorTranslations={getErrorTranslations()}
    >
      <div className="grid w-full gap-4">
        <FormField
          control={form.control}
          name="productId"
          render={() => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.shopify.convertSizeChart.form.product")}
              </FormLabel>
              <FormControl>
                <Combobox
                  options={productOptions}
                  value={selectedProductOption}
                  onChange={handleProductChange}
                  placeholder={t(
                    "settings.integrations.shopify.convertSizeChart.form.productPlaceholder",
                  )}
                  emptyPlaceholder={emptyProductPlaceholder}
                  searchPlaceholder={t(
                    "settings.integrations.shopify.convertSizeChart.form.productSearch",
                  )}
                  searchQuery={productSearchQuery}
                  setSearchQuery={handleProductSearchQueryChange}
                  shouldFilter={false}
                  className="w-full"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="metafieldValue"
          render={({ field }) => (
            <FormItem className="hidden">
              <FormControl>
                <textarea
                  name={field.name}
                  ref={field.ref}
                  value={field.value ?? ""}
                  onBlur={field.onBlur}
                  hidden
                  readOnly
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        {visibleVariants.length > 0 && (
          <FormField
            control={form.control}
            name="variantId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  {t("settings.integrations.shopify.convertSizeChart.form.variant")}
                </FormLabel>
                <FormControl>
                  <Select
                    value={field.value ?? PRODUCT_METAFIELD_VALUE}
                    onValueChange={(value) =>
                      field.onChange(value === PRODUCT_METAFIELD_VALUE ? undefined : value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={t(
                          "settings.integrations.shopify.convertSizeChart.form.variantPlaceholder",
                        )}
                      />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={PRODUCT_METAFIELD_VALUE}>
                        {t("settings.integrations.shopify.convertSizeChart.form.productMetafield")}
                      </SelectItem>
                      {visibleVariants.map((variant) => (
                        <SelectItem key={variant.id} value={variant.id}>
                          {getVariantLabel(variant)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        )}
        <FormField
          control={form.control}
          name="sizeChartMetafieldKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.shopify.convertSizeChart.form.sizeChartJson")}
              </FormLabel>
              <FormControl>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value)
                    setLocalStorageValue(LAST_SIZE_CHART_METAFIELD_KEY_STORAGE_KEY, value)
                  }}
                  disabled={sizeChartMetafieldOptions.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "settings.integrations.shopify.convertSizeChart.form.metafieldPlaceholder",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {sizeChartMetafieldOptions.map((metafield) => (
                      <SelectItem key={metafield.key} value={metafield.key}>
                        {metafield.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              {!isFetchingMetafields && sizeChartMetafieldOptions.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t("settings.integrations.shopify.convertSizeChart.form.noMetafieldsType", {
                    type: SIZE_CHART_METAFIELD_TYPE,
                  })}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="imageMetafieldKey"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                {t("settings.integrations.shopify.convertSizeChart.form.imageMetafield")}
              </FormLabel>
              <FormControl>
                <Select
                  value={field.value}
                  onValueChange={(value) => {
                    field.onChange(value)
                    setLocalStorageValue(LAST_IMAGE_METAFIELD_KEY_STORAGE_KEY, value)
                  }}
                  disabled={imageMetafieldOptions.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(
                        "settings.integrations.shopify.convertSizeChart.form.metafieldPlaceholder",
                      )}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {imageMetafieldOptions.map((metafield) => (
                      <SelectItem key={metafield.key} value={metafield.key}>
                        {metafield.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FormControl>
              {!isFetchingMetafields && imageMetafieldOptions.length === 0 && (
                <p className="text-sm text-muted-foreground">
                  {t("settings.integrations.shopify.convertSizeChart.form.noMetafieldsType", {
                    type: IMAGE_METAFIELD_TYPE,
                  })}
                </p>
              )}
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
      <div>
        <Button
          disabled={
            isUpdatingMetafield ||
            !result ||
            !generatedImage?.base64Image ||
            !form.formState.isValid
          }
        >
          {t("settings.integrations.shopify.convertSizeChart.form.apply")}
        </Button>
      </div>
    </Form>
  )
}
